"use client";
import { cn } from "@/lib/utils";
import Carousel from "@/components/views/list/card/Carousel";
import S3Image from "@/components/S3Image";
import { useEffect, useState } from "react";
import { YouTubeEmbed } from "@next/third-parties/google";
import { useVenueDetail } from "@/hooks/venueContext";
import WhatsappButton from "@/components/WhatsappButton";
import getTopDiscountByVenue from "@/lib/get-top-discount-by-venue";
import { isPhysiotherapistVenue } from "@/lib/venue-business-type";
import {
  WHATSAPP_SECTIONS,
  WHATSAPP_TAGS,
} from "@/@core/enums/whatsapp_tags.enum";
import WhatsappLinkWithBeacon from "@/components/WhatsappLinkWithBeacon";
import GiftIcon from "@heroicons/react/24/solid/GiftIcon";
import TicketIcon from "@heroicons/react/24/solid/TicketIcon";
import { GLOBAL_VENUE_DISCOUNT_PERCENT_KEY } from "@/@core/constants/discount";
import { useTranslation } from "@/hooks/translationContext";
import useWhatsappContext from "@/hooks/useWhatsappContext";
import useGetWpHref from "@/hooks/useGetWpHref";
import WhatsappIcon from "@/components/hero-icons/whatsapp-icon";

interface IBanner {
  url: string;
}

interface Props {
  venueId: number;
  venueTitle: string;
  width: number;
  images: IBanner[];
  videos?: string[];
  slideCount: number;
  setSlideCount: (n: number) => void;
  imageClassNames?: string;
  showButtons?: boolean;
  showContactButtons?: boolean;
  hoverableButtons?: boolean;
}

export default function VenueHeaderPhotosCarousel(props: Props) {
  const {
    venueId,
    width,
    images,
    videos,
    slideCount,
    setSlideCount,
    hoverableButtons = true,
    showContactButtons = false,
  } = props;
  const [currentVideoIndex, setCurrentVideoIndex] = useState<number | null>(
    null,
  );
  const [imageWidth, setImageWidth] = useState(400);
  const { venue } = useVenueDetail();
  const { t } = useTranslation();
  const whatsappContext = useWhatsappContext();
  const getWhatsappHrefUrl = useGetWpHref();

  useEffect(() => {
    setImageWidth(window.innerWidth);
  }, []);

  const getCount = () => {
    let total = (images?.length || 0) + (videos?.length || 0);
    if (showContactButtons && venue?.whatsappPhoneNumber) total++;

    return total;
  };

  const sendYouTubeCommand = (
    iframe: HTMLIFrameElement | null,
    command: "playVideo" | "pauseVideo",
  ) => {
    if (iframe) {
      iframe.contentWindow?.postMessage(
        `{"event":"command","func":"${command}","args":""}`,
        "*",
      );
    }
  };

  useEffect(() => {
    if (currentVideoIndex !== null && videos) {
      const iframe = document.querySelector(
        `iframe[src*="${videos[currentVideoIndex]}"]`,
      ) as HTMLIFrameElement;
      sendYouTubeCommand(iframe, "pauseVideo");
      setCurrentVideoIndex(null);
    }
  }, [slideCount]);

  async function playOrPauseVideo(id) {
    let video = document.querySelector(`lite-youtube[videoid='${id}']`) as any;

    if (!video) {
      console.error("Video not found");
      return;
    }

    let player = await video.getYTPlayer();
    let playerState = player.getPlayerState();

    if (playerState === 1) {
      player.pauseVideo();
    } else {
      player.playVideo();
    }
  }

  const firstDiscount = (venue as Venue.Detail)?.discounts?.[0];
  // Physiotherapist venues carry no discount or campaign CTAs (SAB-362).
  const isPhysiotherapist = isPhysiotherapistVenue(venue);
  const hasCampaign =
    !isPhysiotherapist && (venue as Venue.Detail)?.campaigns?.length > 0;

  const globalDiscountPercent = t(GLOBAL_VENUE_DISCOUNT_PERCENT_KEY);

  const hasDiscount = () => {
    if (isPhysiotherapist) return false;

    const activeDiscounts = (venue as Venue.Detail)?.discounts?.find(
      (d) => d?.percent == 0,
    );
    if (activeDiscounts) return false;

    return (
      t(GLOBAL_VENUE_DISCOUNT_PERCENT_KEY) !==
      GLOBAL_VENUE_DISCOUNT_PERCENT_KEY || firstDiscount?.percent
    );
  };

  const getDiscountPercent = () => {
    return venue?.discounts?.[0]
      ? venue?.discounts?.[0].percent
      : globalDiscountPercent;
  };

  const getWhatsappContext = () => {
    return whatsappContext.getContext({
      venue: venue.descriptions?.[0]?.title,
      "discount-visitors": venue?.discounts?.[0]
        ? venue?.discounts?.[0]?.percent
        : globalDiscountPercent,
    });
  };

  return (
    <Carousel
      count={getCount()}
      itemIndex={slideCount}
      setItemIndex={setSlideCount}
      dotColor="gray"
      showButtons={props.showButtons}
      hoverable={hoverableButtons}
      height="282px"
    >
      <>
        {images.map((banner: IBanner, index) => {
          return (
            <div
              key={index}
              className={cn(
                "relative h-full w-full",
                props.imageClassNames,
              )}
            >
              <S3Image
                fetchPriority={index === 0 ? "high" : "low"}
                loading={index === 0 ? "eager" : "lazy"}
                fill
                unoptimized={true}
                src={`/image/venue/${venue.id}/photo/${banner.url}?w=600&quality=60&format=auto&fit=contain`}
                className={cn("object-contain flex flex-shrink-0")}
                alt={t("venue-detail-photo-alt", {
                  venue: props?.venueTitle,
                  index: index + 1,
                })}
                title={t("venue-detail-photo-alt", {
                  venue: props?.venueTitle,
                  index: index + 1,
                })}
              />
            </div>
          );
        })}

        {Array.isArray(videos) &&
          videos.map((video) => {
            return (
              <div
                key={video}
                className={cn("relative w-full h-full", props.imageClassNames)}
              >
                <div className="relative h-full w-full">
                  <YouTubeEmbed
                    style="width: 100%; height: 100%; min-height: 100%"
                    videoid={video}
                    height={imageWidth}
                    js-api
                    playlabel={t("venue-detail-header-video-play-label")}
                    params="enablejsapi=1&controls=0&showinfo=1"
                  />

                  <div
                    className="absolute inset-0 flex items-center justify-center cursor-pointer z-10"
                    onClick={() => playOrPauseVideo(video)}
                  />
                </div>
              </div>
            );
          })}

        {showContactButtons && venue?.whatsappPhoneNumber && (
          <div
            style={{ height: "309px" }}
            className={cn(
              `flex flex-col gap-4 justify-center items-center px-4 w-full`,
              props.imageClassNames,
            )}
          >
            <span className="whitespace-normal break-words text-lg text-center">
              {venue?.descriptions?.[0]?.title} ile Hemen İletişime Geçin
            </span>

            <div className="flex flex-col gap-4 items-center">
              <WhatsappButton
                translateValues={{
                  "discount-visitors": getTopDiscountByVenue(
                    venue as Venue.List,
                  ),
                }}
                section={WHATSAPP_SECTIONS.VENUE_DETAIL}
                tag={WHATSAPP_TAGS.NORMAL}
                phoneNumber={venue?.whatsappPhoneNumber}
                venue={venue as Venue.List}
                variant="default"
                className="bg-[#25D366] hover:bg-[#25b366] rounded-full text-white flex items-center justify-center
                                    px-[21px] py-3 text-sm w-[253px] text-center relative"
              >
                <WhatsappIcon className="absolute size-4 left-4" />{" "}
                <span className="ml-2">Whatsapp ile Mesaj Gönder</span>
              </WhatsappButton>

              {venue?.whatsappPhoneNumber && hasDiscount() && (
                <WhatsappLinkWithBeacon
                  venueId={venue?.id}
                  section={WHATSAPP_SECTIONS.DISCOUNT}
                  title={`%${getDiscountPercent()} İndirimi Sor`}
                  disabled={!venue?.whatsappPhoneNumber}
                  href={getWhatsappHrefUrl({
                    tag: WHATSAPP_TAGS.DISCOUNT,
                    phone: venue.whatsappPhoneNumber,
                    translateValues: getWhatsappContext(),
                  })}
                >
                  <div className="flex gap-2 text-base items-center font-[500] rounded-lg hover:bg-slate-100 duration-200 p-1 w-full">
                    <GiftIcon className="size-6" />%{getDiscountPercent()}{" "}
                    İndirimi Sor
                  </div>
                </WhatsappLinkWithBeacon>
              )}

              {venue?.whatsappPhoneNumber && hasCampaign && (
                <WhatsappLinkWithBeacon
                  section={WHATSAPP_SECTIONS.CAMPAIGN}
                  title={`Kampanyaları Sor`}
                  disabled={!venue?.whatsappPhoneNumber}
                  venueId={venue?.id}
                  href={getWhatsappHrefUrl({
                    tag: WHATSAPP_TAGS.CAMPAIGN,
                    phone: venue?.whatsappPhoneNumber,
                    translateValues: getWhatsappContext(),
                  })}
                >
                  <div className="flex gap-2 text-start items-center text-base font-medium py-1 w-full text-primary">
                    <TicketIcon className="size-6" />
                    <span className="grow shrink-0">Kampanyaları Sor</span>
                  </div>
                </WhatsappLinkWithBeacon>
              )}
            </div>
          </div>
        )}
      </>
    </Carousel>
  );
}
